/**
 * Admin - Zelle Auto-Verify status page (read-only, minimal).
 *
 * Full pattern-editing UI and test-parse dialog land next turn. This page
 * ships now so admins can:
 *   - See if the Gmail OAuth handshake has been done
 *   - Watch the poller in real time (last_poll_at + next_poll_at)
 *   - See the last 50 auto-verify attempts with status chips
 *   - Trigger a manual "Run poll now" (super admin)
 *   - See the 9 seeded bank patterns as a static list
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, XCircle, AlertTriangle, Info,
  Mail, Clock, Shield, Play, RefreshCcw,
  Landmark as BankIcon,
} from 'lucide-react';
import api from '@/utils/api';
import { useNavigate } from 'react-router-dom';

const STATUS_META = {
  verified:                { color: 'success', label: 'Verified',        Icon: CheckCircle2 },
  skipped_no_match:        { color: 'neutral', label: 'No match',        Icon: Info },
  skipped_ambiguous:       { color: 'warning', label: 'Ambiguous',       Icon: AlertTriangle },
  skipped_amount_off:      { color: 'warning', label: 'Amount off',      Icon: AlertTriangle },
  skipped_no_bank_match:   { color: 'neutral', label: 'No bank',         Icon: Info },
  skipped_no_reference:    { color: 'neutral', label: 'No reference',    Icon: Info },
  skipped_no_amount:       { color: 'neutral', label: 'No amount',       Icon: Info },
  skipped_no_expected_amount: { color: 'neutral', label: 'No expected amount', Icon: Info },
  skipped_registration_state: { color: 'neutral', label: 'Bad state', Icon: Info },
  skipped_rate_limit:      { color: 'warning', label: 'Rate limited',    Icon: AlertTriangle },
  skipped_already_processed: { color: 'neutral', label: 'Already seen', Icon: Info },
  error:                   { color: 'error',   label: 'Error',           Icon: XCircle },
  in_progress:             { color: 'neutral', label: 'In progress',     Icon: Clock },
};

const AdminZelleAutoVerifyPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [oauth, setOauth] = useState(null);           // Phase C
  const [oauthBusy, setOauthBusy] = useState(false);
  const [patterns, setPatterns] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pollBusy, setPollBusy] = useState(false);
  const [pollMsg, setPollMsg] = useState('');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [s, o, p, l] = await Promise.all([
        api.get('/admin/zelle/status'),
        api.get('/admin/zelle/oauth/status'),
        api.get('/admin/zelle/patterns'),
        api.get('/admin/zelle/verify-log?limit=50'),
      ]);
      setStatus(s.data);
      setOauth(o.data);
      setPatterns(p.data.patterns || []);
      setLog(l.data.entries || []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // One-shot toast from ?oauth=... on landing after a Google redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('oauth');
    if (!flag) return;
    if (flag === 'connected') {
      const em = params.get('email') || 'Gmail';
      setToast({ kind: 'success', msg: `Gmail connected as ${em}.` });
    } else if (flag === 'denied') {
      const reason = params.get('reason') || '';
      const humanReason = {
        not_super_admin: 'this account is not a super admin',
        user_not_found: 'the admin account referenced by the OAuth state was not found (Google-Auth cookie may have shadowed your password login; sign out of Google Auth on this domain and retry)',
        user_inactive: 'the admin account is deactivated',
        invalid_state: 'the OAuth state token expired or was tampered with (try again from the Connect Gmail button)',
        missing_params: 'Google returned without the required parameters',
        server_misconfigured: 'GOOGLE_OAUTH_CLIENT_ID or SECRET is not set on the server',
        token_exchange_failed: 'the token exchange with Google failed at the network layer',
        token_exchange_error: 'Google refused the token exchange (redirect_uri or client mismatch)',
      }[reason] || reason;
      setToast({ kind: 'error', msg: `Gmail connection was declined${humanReason ? ` - ${humanReason}` : ''}.` });
    } else if (flag === 'no_refresh_token') {
      setToast({ kind: 'error', msg: 'Google did not return a refresh token. Revoke prior access in your Google account and retry, or use a fresh Google account.' });
    }
    const url = window.location.pathname;
    window.history.replaceState({}, '', url);
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, []);

  // Live-refresh status every 15s so admins can watch the poller tick.
  useEffect(() => {
    const t = setInterval(() => { load(); }, 15000);
    return () => clearInterval(t);
  }, [load]);

  const startOAuth = async () => {
    setOauthBusy(true);
    try {
      const { data } = await api.get('/admin/zelle/oauth/start');
      if (data?.auth_url) window.location.assign(data.auth_url);
    } catch (e) {
      setToast({ kind: 'error', msg: e?.response?.data?.detail || 'Could not start OAuth flow' });
      setOauthBusy(false);
    }
  };

  const disconnectOAuth = async () => {
    setOauthBusy(true);
    try {
      await api.post('/admin/zelle/oauth/disconnect');
      setToast({ kind: 'success', msg: 'Gmail disconnected.' });
      await load();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.response?.data?.detail || 'Disconnect failed' });
    } finally {
      setOauthBusy(false);
    }
  };

  const runPollNow = async () => {
    setPollBusy(true); setPollMsg('');
    try {
      const { data } = await api.post('/admin/zelle/run-poll-now');
      setPollMsg(data?.summary?.reason
        ? `Poll ran: ${data.summary.reason}`
        : `Poll ran. ${data?.summary?.seen_messages ?? 0} messages seen.`);
      await load();
    } catch (e) {
      setPollMsg(e?.response?.data?.detail || 'Poll failed. Try again.');
    } finally {
      setPollBusy(false);
    }
  };

  const gmailConnected = status?.gmail_connected;
  const authState = status?.oauth_auth_state || {};
  const needsReauth = !!authState.needs_reauth;

  const startReauth = async () => {
    try {
      const { data } = await api.get('/admin/zelle/oauth/start');
      if (data?.authorization_url) window.location.href = data.authorization_url;
    } catch (e) {
      setToast({ kind: 'error', msg: 'Could not start re-authorization flow' });
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-zelle-page">
        <header className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold text-primary-900">Payment auto-verify</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Poll a Gmail inbox every {status?.poll_interval_minutes || 5} minutes for Zelle and Venmo payment notifications, extract the amount + reference, and flag matching RSVPs for admin confirmation.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={busy} data-testid="reload-btn">
            <RefreshCcw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </header>

        {needsReauth && (
          <div
            className="p-4 rounded-md border-2 border-error-300 bg-error-50 flex items-start gap-3"
            data-testid="zelle-oauth-reauth-banner"
          >
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-error-900">
                Zelle Gmail poller offline. Re-authorize now.
              </div>
              <div className="text-sm text-error-800 mt-1">
                Google refused the refresh token. New Zelle payments are NOT being auto-verified until you re-connect. Last error: <code className="text-xs">{authState.last_error || 'invalid_grant'}</code>
              </div>
            </div>
            <Button
              onClick={startReauth}
              className="bg-error-700 hover:bg-error-800 text-white flex-shrink-0"
              data-testid="zelle-oauth-reauth-button"
            >
              Re-authorize
            </Button>
          </div>
        )}

        {/* Phase C: Gmail OAuth connection card */}
        {toast && (
          <div className={`p-3 rounded-md border text-sm ${toast.kind === 'success'
            ? 'bg-success-50 border-success-300 text-success-900'
            : 'bg-error-50 border-error-300 text-error-900'}`} data-testid="oauth-toast">
            {toast.msg}
          </div>
        )}

        <Card className="border shadow-soft">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary-800" />
              <div className="text-[11px] uppercase tracking-widest text-secondary-800 font-semibold">
                Gmail connection
              </div>
            </div>
            {oauth?.connected ? (
              <div className="space-y-3" data-testid="oauth-connected">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="outline" className="bg-success-50 border-success-300 text-success-800 gap-1.5">
                    <CheckCircle2 className="h-3 w-3" /> Connected
                  </Badge>
                  <span className="text-sm text-neutral-800 font-mono" data-testid="oauth-email">
                    {oauth.google_email_masked || '(unknown)'}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${oauth.token_healthy
                    ? 'bg-success-50 border-success-300 text-success-800'
                    : 'bg-error-50 border-error-300 text-error-800'}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${oauth.token_healthy ? 'bg-success-600' : 'bg-error-600'}`} />
                    {oauth.token_healthy ? 'Token healthy' : 'Token unhealthy'}
                  </span>
                </div>
                <div className="text-xs text-neutral-600">
                  Connected {fmtTime(oauth.connected_at)} · Scopes: {oauth.scopes?.map((s) => s.split('/').pop()).join(', ') || '-'}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={startOAuth} disabled={oauthBusy} data-testid="oauth-reconnect-btn">
                    <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Reconnect
                  </Button>
                  <Button variant="outline" size="sm" onClick={disconnectOAuth} disabled={oauthBusy}
                          className="border-error-300 text-error-700 hover:bg-error-50" data-testid="oauth-disconnect-btn">
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="oauth-disconnected">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-warning-50 border-warning-300 text-warning-800 gap-1.5">
                    <AlertTriangle className="h-3 w-3" /> Not connected
                  </Badge>
                  <span className="text-xs text-neutral-600">
                    The poller cannot read any Gmail inbox until an admin connects one.
                  </span>
                </div>
                <div>
                  <Button onClick={startOAuth} disabled={oauthBusy}
                          className="bg-primary-800 hover:bg-primary-900 text-white"
                          data-testid="oauth-connect-btn">
                    <Mail className="h-4 w-4 mr-1.5" /> {oauthBusy ? 'Redirecting…' : 'Connect Gmail'}
                  </Button>
                </div>
                <div className="text-[11px] text-neutral-500 leading-relaxed">
                  You&apos;ll be redirected to Google&apos;s consent screen. Sign in with the inbox that receives your Zelle deposit notifications, then approve read-only Gmail access. You&apos;ll land back here automatically.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 text-xs border-t border-neutral-100">
              <StatCell label="OAuth client id" ok={status?.oauth_client_id_present} />
              <StatCell label="OAuth client secret" ok={status?.oauth_client_secret_present} />
              <StatCell label="Refresh token" ok={oauth?.connected} />
              <StatCell label="Verified this hour" value={`${status?.verified_last_hour ?? 0} / ${status?.global_max_verifies_per_hour ?? '-'}`} />
            </div>
            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-neutral-100">
              <div className="text-xs text-neutral-600">
                <Clock className="h-3 w-3 inline mr-1" />
                Last poll: <strong>{fmtTime(status?.last_poll_at) || 'not yet'}</strong>
              </div>
              <div className="text-xs text-neutral-600">
                Next poll: <strong>{fmtTime(status?.next_poll_at) || '-'}</strong>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {pollMsg && <span className="text-xs text-neutral-700">{pollMsg}</span>}
                <Button
                  size="sm"
                  onClick={runPollNow}
                  disabled={pollBusy}
                  className="bg-primary-800 hover:bg-primary-900 text-white"
                  data-testid="run-poll-now-btn"
                >
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  {pollBusy ? 'Polling…' : 'Run poll now'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Safety guardrails */}
        <Card className="border shadow-soft">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary-800" />
              <div className="text-[11px] uppercase tracking-widest text-secondary-800 font-semibold">
                Safety guardrails
              </div>
            </div>
            <ul className="text-sm text-neutral-800 space-y-1 list-disc pl-5">
              <li>Exact reference match required (case-sensitive, e.g. <code className="text-[11px]">DAL-A1B2C3D4</code>)</li>
              <li>Amount within ±{Math.round((status?.amount_tolerance_pct ?? 0.05) * 100)}% of expected</li>
              <li>Skipped if 2+ registrations share the reference (ambiguous, admin resolves)</li>
              <li>Global cap of {status?.global_max_verifies_per_hour ?? 100} auto-verifies per hour</li>
              <li>Every verify writes an <code className="text-[11px]">admin_audit_logs</code> entry with the Gmail messageId</li>
            </ul>
          </CardContent>
        </Card>

        {/* Verify log */}
        <Card className="border shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="text-[11px] uppercase tracking-widest text-secondary-800 font-semibold">
                  Recent auto-verify attempts
                </div>
                <Badge variant="outline" className="text-[10px]">{log.length}</Badge>
              </div>
              <div className="text-xs text-neutral-500">Last 50, newest first</div>
            </div>
            {log.length === 0 ? (
              <div className="text-sm text-neutral-500 italic py-8 text-center" data-testid="verify-log-empty">
                No poll attempts logged yet. Once Gmail is connected and the poller runs, entries will appear here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                      <th className="text-left font-medium py-2 pr-3">Status</th>
                      <th className="text-left font-medium py-2 pr-3">When</th>
                      <th className="text-left font-medium py-2 pr-3">Bank</th>
                      <th className="text-left font-medium py-2 pr-3">Amount</th>
                      <th className="text-left font-medium py-2 pr-3">Reference</th>
                      <th className="text-left font-medium py-2 pr-3">Reg</th>
                      <th className="text-left font-medium py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((e) => {
                      const meta = STATUS_META[e.status] || { color: 'neutral', label: e.status, Icon: Info };
                      const { Icon } = meta;
                      return (
                        <tr key={e.id} className="border-b border-neutral-100" data-testid={`verify-log-row-${e.id}`}>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className={`text-[10px] gap-1 border-${meta.color}-300 bg-${meta.color}-50 text-${meta.color}-800`}>
                              <Icon className="h-3 w-3" /> {meta.label}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-xs text-neutral-600 whitespace-nowrap">{fmtTime(e.created_at)}</td>
                          <td className="py-2 pr-3 text-xs">{e.matched_bank_id || '-'}</td>
                          <td className="py-2 pr-3 text-xs tabular-nums">{e.extracted_amount != null ? `$${Number(e.extracted_amount).toFixed(2)}` : '-'}</td>
                          <td className="py-2 pr-3 text-xs font-mono">{e.extracted_reference || '-'}</td>
                          <td className="py-2 pr-3 text-xs">
                            {e.matched_registration_id ? (
                              <button
                                className="text-primary-800 hover:underline font-mono"
                                onClick={() => navigate(`/admin/registrations/${e.matched_registration_id}`)}
                              >
                                {e.matched_registration_id.slice(0, 8)}…
                              </button>
                            ) : '-'}
                          </td>
                          <td className="py-2 text-xs text-neutral-600 max-w-[240px] truncate" title={e.notes}>{e.notes || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seeded patterns (read-only preview; editor lands in a later phase) */}
        <Card className="border shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BankIcon className="h-4 w-4 text-primary-800" />
                <div className="text-[11px] uppercase tracking-widest text-secondary-800 font-semibold">
                  Payment patterns ({patterns.length} seeded)
                </div>
              </div>
              <Badge variant="outline" className="text-[10px] border-secondary-300 text-secondary-800">
                Editor coming next phase
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {patterns.map((p) => (
                <div key={p.id} className="p-3 rounded-md border border-neutral-200 bg-neutral-50/50" data-testid={`bank-preview-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-primary-900">{p.bank_name}</div>
                    <div className="flex items-center gap-1">
                      <Badge
                        variant="outline"
                        className={`text-[9px] uppercase tracking-wider ${p.service === 'venmo'
                          ? 'border-blue-300 text-blue-800 bg-blue-50'
                          : 'border-purple-300 text-purple-800 bg-purple-50'}`}
                      >
                        {p.service || 'zelle'}
                      </Badge>
                      {p.enabled ? (
                        <Badge variant="outline" className="text-[10px] border-success-300 text-success-800 bg-success-50">Enabled</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-neutral-300 text-neutral-600">Disabled</Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-neutral-500 font-mono mt-1 truncate" title={p.sender_regex}>
                    {p.sender_regex}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
  );
};

const StatCell = ({ label, ok, value }) => (
  <div className="p-2 rounded-md bg-neutral-50 border border-neutral-100">
    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-medium">{label}</div>
    <div className="mt-0.5 flex items-center gap-1.5">
      {typeof ok === 'boolean' ? (
        ok ? <CheckCircle2 className="h-3.5 w-3.5 text-success-600" /> : <XCircle className="h-3.5 w-3.5 text-error-600" />
      ) : null}
      <div className="text-xs text-neutral-800 font-medium tabular-nums">
        {value != null ? value : (ok ? 'Set' : 'Missing')}
      </div>
    </div>
  </div>
);

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
};

export default AdminZelleAutoVerifyPage;
